import { useEffect, useState, type ImgHTMLAttributes } from "react";

/**
 * Every image on this site is hotlinked from the tenant's Firebase Storage
 * bucket. When that bucket is slow, blocked by a corporate proxy, or the item
 * simply has no photo, the browser paints its own broken-image glyph — which
 * looks like the site is broken rather than like a photo is missing.
 *
 * `SafeImage` swaps in a locally-served placeholder instead. The placeholders
 * live in `public/assets/placeholders/` so they keep working with no network
 * at all, which is also what makes the screenshot pass render honestly.
 */

export const PLACEHOLDER = {
  food: "/assets/placeholders/food.svg",
  cover: "/assets/placeholders/cover.svg",
  logo: "/assets/placeholders/logo.svg",
  scooter: "/assets/placeholders/scooter.svg",
  generic: "/assets/placeholders/generic.svg",
} as const;

export type PlaceholderKind = keyof typeof PLACEHOLDER;

export interface SafeImageProps extends Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src"
> {
  src?: string | null;
  /** Which placeholder to fall back to. Defaults to the food thumbnail. */
  placeholder?: PlaceholderKind;
}

export default function SafeImage({
  src,
  placeholder = "food",
  alt = "",
  ...props
}: SafeImageProps) {
  const fallback = PLACEHOLDER[placeholder];
  const [current, setCurrent] = useState(src || fallback);

  // A new item can reuse the same mounted <img>, so reset when the source
  // changes — otherwise one failed load poisons every subsequent item.
  useEffect(() => {
    setCurrent(src || fallback);
  }, [src, fallback]);

  const showingPlaceholder = current === fallback;

  return (
    <img
      {...props}
      src={current}
      alt={alt}
      // The theme dims placeholder art in dark mode via this attribute.
      data-placeholder={showingPlaceholder ? "true" : undefined}
      onError={() => {
        if (current !== fallback) setCurrent(fallback);
      }}
    />
  );
}
